import { Collapse, Space } from "antd";
import { type ReactNode, useCallback, useMemo } from "react";

import { BTSection, BTText } from "@btnet/core-components";
import { useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2 } from "@btnet/services-btnet/handlers/entity-records/entity-records";
import type { EntityRecordResponse } from "@btnet/services-btnet/handlers/models/entityRecordResponse";
import { EntityType } from "@btnet/services-btnet/handlers/models/entityType";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { getRelatedItemLink } from "utilities/relatedItem/relatedItem";

import { BTCollapse } from "commonComponents/btWrappers/BTCollapse/BTCollapse";
import { BTIconCaretRightOutlined } from "commonComponents/btWrappers/BTIcon";
import { type IEntityLinkTagProps } from "commonComponents/entity/entityLinkSelector/EntityLinkSelector.utilities";
import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { RelatedItemTag } from "commonComponents/entity/relatedItem/RelatedItemTag/RelatedItemTag";
import {
    getStatusTextForSelectionOption,
    getStatusTypeForSelectionOption,
} from "commonComponents/utilities/SelectionOption/SelectionOption.utils";
import { type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import {
    getBidPackageReleasedStatusText,
    getBidPackageReleasedStatusType,
} from "entity/bidPackage/BidPackage/bidPackageReleasedStatus.utils";
import {
    getPurchaseOrderApprovalStatusText,
    getPurchaseOrderApprovalStatusType,
} from "entity/purchaseOrder/common/purchaseOrderApprovalStatus.utils";

interface IExpandIconProps {
    isActive?: boolean;
}

function ExpandIcon({ isActive }: Readonly<IExpandIconProps>) {
    return <BTIconCaretRightOutlined rotateRight={isActive} />;
}

function renderStatusBadge(
    status: number | null,
    getStatusText: (status: number) => string,
    getStatusType: (status: number) => StatusTypes,
): ReactNode | undefined {
    if (status === null) {
        return undefined;
    }

    return (
        <StatusTagDisplay statusType={getStatusType(status)} statusText={getStatusText(status)} />
    );
}

function renderBidPackageStatusBadge(status: number | null): ReactNode | undefined {
    return renderStatusBadge(
        status,
        getBidPackageReleasedStatusText,
        getBidPackageReleasedStatusType,
    );
}

function renderPurchaseOrderStatusBadge(status: number | null): ReactNode | undefined {
    return renderStatusBadge(
        status,
        getPurchaseOrderApprovalStatusText,
        getPurchaseOrderApprovalStatusType,
    );
}

function makeRenderSelectionOptionStatusBadge(isBuilderPortal: boolean) {
    return (status: number | null): ReactNode | undefined =>
        renderStatusBadge(
            status,
            (s) => getStatusTextForSelectionOption(s, isBuilderPortal),
            (s) => getStatusTypeForSelectionOption(s, isBuilderPortal),
        );
}

function mapEntitiesToTagProps(
    entities: EntityRecordResponse[],
    relatedItemType: RelatedItemType,
    jobId: number,
    renderStatus?: (status: number | null) => ReactNode | undefined,
    isToFullUrl: boolean = false,
): IEntityLinkTagProps[] {
    return entities.map((entity) => {
        const route = getRelatedItemLink(entity.entityId, relatedItemType, jobId);
        return {
            id: entity.entityId,
            externalId: entity.id,
            itemType: relatedItemType,
            subject: entity.title,
            canRemove: false,
            onRemove: () => {},
            to: typeof route === "string" ? route : route.pathname,
            isToFullUrl,
            status: renderStatus?.(entity.status) ?? undefined,
        };
    });
}

interface ILinkedItemsSectionProps {
    specificationExternalId: string;
    jobId: number;
    isMobileWebview?: boolean;
}

export function LinkedItemsSection({
    specificationExternalId,
    jobId,
    isMobileWebview = false,
}: Readonly<ILinkedItemsSectionProps>) {
    const canViewBidPackage = usePermission("bidding", "canView");
    const canViewPurchaseOrder = usePermission("purchaseOrders", "canView");
    const canViewSelections = usePermission("selections", "canView");
    const canViewEstimates = usePermission("estimates", "canView");
    const userInfo = useRequiredContext(UserInfoContext);
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const isBuilderPortal = userInfo.loginType === BTLoginTypes.BUILDER;
    const renderSelectionOptionStatusBadge = useMemo(
        () => makeRenderSelectionOptionStatusBadge(isBuilderPortal),
        [isBuilderPortal],
    );

    const queryEnabled = Boolean(specificationExternalId);

    const { data: batchData } = useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2(
        specificationExternalId,
        {
            targetEntityTypes: [
                EntityType.BidPackage,
                EntityType.PurchaseOrder,
                EntityType.SelectionOptions,
                EntityType.ProposalFormatItem,
            ],
        },
        { query: { enabled: queryEnabled } },
    );

    const entitiesByType = batchData?.data?.entitiesByType;

    const getEntitiesForType = useCallback(
        (entityType: EntityType): EntityRecordResponse[] =>
            (entitiesByType?.find((e) => e.entityType === entityType)?.entityRecords ??
                []) as EntityRecordResponse[],
        [entitiesByType],
    );

    const bidPackageTagProps = useMemo(
        () =>
            mapEntitiesToTagProps(
                getEntitiesForType(EntityType.BidPackage),
                RelatedItemType.BidPackage,
                jobId,
                renderBidPackageStatusBadge,
            ),
        [getEntitiesForType, jobId],
    );

    const purchaseOrderTagProps = useMemo(
        () =>
            mapEntitiesToTagProps(
                getEntitiesForType(EntityType.PurchaseOrder),
                RelatedItemType.PurchaseOrder,
                jobId,
                renderPurchaseOrderStatusBadge,
            ),
        [getEntitiesForType, jobId],
    );

    const selectionOptionTagProps = useMemo(
        () =>
            mapEntitiesToTagProps(
                getEntitiesForType(EntityType.SelectionOptions),
                RelatedItemType.SelectionOption,
                jobId,
                renderSelectionOptionStatusBadge,
            ),
        [getEntitiesForType, jobId, renderSelectionOptionStatusBadge],
    );

    // Estimate groups and cost groups are both stored as ProposalFormatItem entity records; they are
    // distinguished by record subtype — estimate groups are job-scoped ("job"), cost groups are job-less
    // ("nullableJob"). Split the single bucket so each renders under its own group and route.
    const proposalFormatItems = getEntitiesForType(EntityType.ProposalFormatItem);
    const globalSpecCostGroupLinkingFlagEnabled = !!builderInfo.flags.globalSpecCostGroupLinking;

    const estimateGroupTagProps = useMemo(
        () =>
            mapEntitiesToTagProps(
                proposalFormatItems.filter((e) => e.entityRecordResponseType !== "nullableJob"),
                RelatedItemType.Estimate,
                jobId,
                undefined,
                true,
            ),
        [proposalFormatItems, jobId],
    );

    const costGroupTagProps = useMemo(
        () =>
            globalSpecCostGroupLinkingFlagEnabled
                ? mapEntitiesToTagProps(
                      proposalFormatItems.filter(
                          (e) => e.entityRecordResponseType === "nullableJob",
                      ),
                      RelatedItemType.CostGroup,
                      jobId,
                  )
                : [],
        [proposalFormatItems, jobId, globalSpecCostGroupLinkingFlagEnabled],
    );

    const showBids = bidPackageTagProps.length > 0;
    const showPurchaseOrders = purchaseOrderTagProps.length > 0;
    const showSelectionOptions = selectionOptionTagProps.length > 0;
    const showEstimateGroups = estimateGroupTagProps.length > 0;
    const showCostGroups = costGroupTagProps.length > 0;

    if (
        !showBids &&
        !showPurchaseOrders &&
        !showSelectionOptions &&
        !showEstimateGroups &&
        !showCostGroups
    ) {
        return null;
    }

    const isBidDisabled = !canViewBidPackage;
    const isPurchaseOrderDisabled = !canViewPurchaseOrder;
    const isSelectionOptionDisabled = !canViewSelections;
    const isEstimateGroupDisabled = !canViewEstimates || isMobileWebview;

    return (
        <BTSection data-testid="specificationLinkedItemsSection" className="pt-3 px-0 pb-0">
            <BTSection.Header>
                <BTText size="distinct-md" tag="h2">
                    Linked Items
                </BTText>
            </BTSection.Header>
            <BTCollapse
                data-testid="specificationsLinkedItemsCollapse"
                bordered={false}
                expandIcon={ExpandIcon}
                className="specificationsLinkedItemsCollapse bds-w-full"
            >
                {showBids && (
                    <Collapse.Panel
                        className="specificationsLinkedItems"
                        key="Bids"
                        header={<BTText text="Bids" size="distinct-md" tag="h2" />}
                    >
                        <Space size="small" direction="vertical" wrap>
                            {bidPackageTagProps.map((tag) => (
                                <RelatedItemTag
                                    key={tag.id}
                                    data-testid={`related-bidPackage-${tag.id}`}
                                    isDisabled={isBidDisabled}
                                    subject={tag.subject}
                                    status={tag.status}
                                    to={tag.to}
                                    isToFullUrl={tag.isToFullUrl}
                                    itemType={tag.itemType}
                                    useMultiTagMargins
                                />
                            ))}
                        </Space>
                    </Collapse.Panel>
                )}
                {showPurchaseOrders && (
                    <Collapse.Panel
                        className="specificationsLinkedItems"
                        key="PurchaseOrders"
                        header={<BTText text="Purchase Orders" size="distinct-md" tag="h2" />}
                    >
                        <Space size="small" direction="vertical" wrap>
                            {purchaseOrderTagProps.map((tag) => (
                                <RelatedItemTag
                                    key={tag.id}
                                    data-testid={`related-purchaseOrder-${tag.id}`}
                                    isDisabled={isPurchaseOrderDisabled}
                                    subject={tag.subject}
                                    status={tag.status}
                                    to={tag.to}
                                    isToFullUrl={tag.isToFullUrl}
                                    itemType={tag.itemType}
                                    useMultiTagMargins
                                />
                            ))}
                        </Space>
                    </Collapse.Panel>
                )}
                {showSelectionOptions && (
                    <Collapse.Panel
                        className="specificationsLinkedItems"
                        key="SelectionOptions"
                        header={<BTText text="Selection Options" size="distinct-md" tag="h2" />}
                    >
                        <Space size="small" direction="vertical" wrap>
                            {selectionOptionTagProps.map((tag) => (
                                <RelatedItemTag
                                    key={tag.id}
                                    data-testid={`related-selectionOption-${tag.id}`}
                                    isDisabled={isSelectionOptionDisabled}
                                    subject={tag.subject}
                                    status={tag.status}
                                    to={tag.to}
                                    isToFullUrl={tag.isToFullUrl}
                                    itemType={tag.itemType}
                                    useMultiTagMargins
                                />
                            ))}
                        </Space>
                    </Collapse.Panel>
                )}
                {showEstimateGroups && (
                    <Collapse.Panel
                        className="specificationsLinkedItems"
                        key="EstimateGroups"
                        header={<BTText text="Estimate Groups" size="distinct-md" tag="h2" />}
                    >
                        <Space size="small" direction="vertical" wrap>
                            {estimateGroupTagProps.map((tag) => (
                                <RelatedItemTag
                                    key={tag.id}
                                    data-testid={`related-estimateGroup-${tag.id}`}
                                    isDisabled={isEstimateGroupDisabled}
                                    subject={tag.subject}
                                    to={tag.to}
                                    isToFullUrl={tag.isToFullUrl}
                                    itemType={tag.itemType}
                                    useMultiTagMargins
                                />
                            ))}
                        </Space>
                    </Collapse.Panel>
                )}
                {showCostGroups && (
                    <Collapse.Panel
                        className="specificationsLinkedItems"
                        key="CostGroups"
                        header={<BTText text="Cost Groups" size="distinct-md" tag="h2" />}
                    >
                        <Space size="small" direction="vertical" wrap>
                            {costGroupTagProps.map((tag) => (
                                <RelatedItemTag
                                    key={tag.id}
                                    data-testid={`related-costGroup-${tag.id}`}
                                    subject={tag.subject}
                                    to={tag.to}
                                    isToFullUrl={tag.isToFullUrl}
                                    itemType={tag.itemType}
                                    useMultiTagMargins
                                />
                            ))}
                        </Space>
                    </Collapse.Panel>
                )}
            </BTCollapse>
        </BTSection>
    );
}
